import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

const navLinks = [
  { label: 'Home', href: '/' },
  { label: 'Our Story', href: '/about' },
  { label: 'Services', href: '/services' },
  { label: 'Contact', href: '/contact' },
  { label: 'Our Rules', href: '/house-rules' },
];

export default function Navigation() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setMobileOpen(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [location.pathname]);

  const isActive = (href: string) => {
    if (href === '/') return location.pathname === '/';
    return location.pathname === href;
  };

  return (
    <div
      className="fixed top-0 left-0 right-0 z-50 px-5 md:px-8"
      style={{ paddingTop: '20px' }}
    >
      <nav
        className="bg-white"
        style={{
          boxShadow: '0 4px 24px 0 rgba(0,0,0,0.05)',
          borderBottom: '1px solid #000000',
        }}
      >
        <div className="w-full px-6 md:px-10 lg:px-16">
          <div className="flex items-center justify-between h-[76px] md:h-[84px]">

            {/* Wordmark */}
            <Link to="/" className="flex items-center group">
              <span
                style={{
                  fontFamily: '\'Fraunces\', \'Playfair Display\', serif',
                  fontWeight: 900,
                  fontSize: 'clamp(30px, 3.2vw, 44px)',
                  letterSpacing: '-0.01em',
                  color: '#000000',
                  lineHeight: 1,
                  display: 'flex',
                  alignItems: 'baseline',
                  gap: '0',
                }}
              >
                <span style={{ fontVariationSettings: '\'opsz\' 144' }}>Paw</span>
                {/* Expressive ampersand — deeply italic, decorative, calligraphic */}
                <span
                  style={{
                    fontFamily: '\'Playfair Display\', \'Fraunces\', serif',
                    fontWeight: 400,
                    fontStyle: 'italic',
                    fontSize: '1.5em',
                    letterSpacing: '-0.06em',
                    lineHeight: 0.8,
                    display: 'inline-block',
                    transform: 'translateY(0.09em) skewX(-8deg)',
                    margin: '0 0.02em 0 0.04em',
                    color: '#000000',
                    opacity: 0.85,
                  }}
                >&amp;</span>
                <span style={{ fontVariationSettings: '\'opsz\' 144' }}>Co.</span>
              </span>
            </Link>

            {/* Desktop Nav Links */}
            <div className="hidden md:flex items-center gap-8 lg:gap-10">
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  className="whitespace-nowrap transition-opacity duration-200"
                  style={{
                    fontFamily: '\'Space Grotesk\', sans-serif',
                    fontWeight: 800,
                    fontSize: '18px',
                    color: '#000000',
                    letterSpacing: '0.01em',
                    textDecoration: isActive(link.href) ? 'underline' : 'none',
                    textDecorationThickness: isActive(link.href) ? '2px' : undefined,
                    textUnderlineOffset: isActive(link.href) ? '5px' : undefined,
                    opacity: isActive(link.href) ? 1 : undefined,
                  }}
                  onMouseEnter={(e) => { if (!isActive(link.href)) (e.currentTarget as HTMLElement).style.opacity = '0.5'; }}
                  onMouseLeave={(e) => { if (!isActive(link.href)) (e.currentTarget as HTMLElement).style.opacity = '1'; }}
                >
                  {link.label}
                </Link>
              ))}
            </div>

            {/* Mobile Hamburger */}
            <button
              className="md:hidden w-10 h-10 flex items-center justify-center"
              style={{ color: '#000000' }}
              onClick={() => setMobileOpen(!mobileOpen)}
              aria-label="Toggle menu"
            >
              <i className={`ri-${mobileOpen ? 'close' : 'menu'}-line text-2xl`} />
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileOpen && (
          <div className="md:hidden bg-white" style={{ borderTop: '1px solid #000000' }}>
            <div className="px-6 py-7 flex flex-col gap-5">
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  className="whitespace-nowrap transition-opacity duration-200"
                  style={{
                    fontFamily: '\'Space Grotesk\', sans-serif',
                    fontWeight: 800,
                    fontSize: '18px',
                    color: '#000000',
                    textDecoration: isActive(link.href) ? 'underline' : 'none',
                    textUnderlineOffset: '5px',
                  }}
                >
                  {link.label}
                </Link>
              ))}
            </div>
          </div>
        )}
      </nav>
    </div>
  );
}
